import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * One step inside Gumloop's steps block (their compact element), measured: a
 * 20px cell holding a 12px muted icon, 4px, then the label at 12/16 500 in the
 * foreground at 80%, truncated to one line, and how long the step took at the
 * right, muted; detail lines under it at 12/16 muted, indented 26px, 4px
 * apart, each on one line.
 */
export function StepRow({
  icon,
  label,
  details,
  time,
  className,
}: {
  icon: IconDefinition;
  label: ReactNode;
  details?: ReactNode[];
  /** How long the step took ("2.3s"); none while unknown. */
  time?: string;
  className?: string;
}) {
  const lines = (details ?? []).filter(
    (line) => line !== null && line !== undefined && line !== ''
  );
  return (
    <div
      data-step-row
      className={cn(
        'flex w-full min-w-0 flex-col items-start gap-[2px] text-[12px] leading-[16px] text-muted-foreground',
        className
      )}
    >
      <div
        className={cn(
          'flex max-w-full items-center gap-[4px]',
          time && 'w-full'
        )}
      >
        <span className='grid size-[20px] shrink-0 place-items-center text-muted-foreground'>
          <AppIcon icon={icon} style={{ fontSize: 12 }} />
        </span>
        <span className='min-w-0 truncate font-medium text-foreground/80'>
          {label}
        </span>
        {time ? (
          <span
            data-step-time
            className='ml-auto shrink-0 pl-[8px] text-muted-foreground tabular-nums'
          >
            {time}
          </span>
        ) : null}
      </div>
      {lines.length > 0 ? (
        <div className='w-full min-w-0 pl-[26px]'>
          <div className='flex min-w-0 flex-col gap-[4px] pt-[2px] text-muted-foreground'>
            {lines.map((line, index) => (
              <span
                key={index}
                className='line-clamp-1 [overflow-wrap:anywhere]'
              >
                {line}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
